import { action, computed, makeObservable } from 'mobx';

import { ENDPOINTS } from '@/configs/endpoints';
import { NewsCategoryOption, NewsCategoryOptionApi, newsCategoryOptionSchema } from '@/entities/News/NewsCategory';
import { FieldSelectMultiStore, FieldSelectStoreOptions } from '@/stores/local/shared/form/FieldSelectStore';
import call from '@/utils/api';
import type { BaseResponse } from '@/utils/api/types';
import { schemeParseMultiple } from '@/utils/schemeParse';

import { ALL_NEWS_CATEGORY_KEY, ALL_NEWS_CATEGORY_OPTION } from './config';

type ConstructorParams = {
  organizationId: number;
};

type GetOptionsParams = Parameters<FieldSelectStoreOptions<NewsCategoryOption>['getOptions']>[0];

export class NewsCategoriesStore extends FieldSelectMultiStore<NewsCategoryOption> {
  constructor({ organizationId }: ConstructorParams) {
    super({
      defaultValue: [ALL_NEWS_CATEGORY_OPTION],
      getOptions: (params) => NewsCategoriesStore._getOptions(params, organizationId),
    });

    makeObservable(this, {
      categories: computed,
      isSelectedAllCategories: computed,
      toggle: action.bound,
    });
  }

  get categories(): NewsCategoryOption[] {
    return this.options;
  }

  get isSelectedAllCategories(): boolean {
    return this.valueKeysSet.has(ALL_NEWS_CATEGORY_KEY);
  }

  async load(forceReset = false): Promise<void> {
    await super.load(true);

    if (forceReset) {
      this.reset();

      return;
    }

    const availableKeys = new Set(this.options.map(({ key }) => key));
    const preserved = this.value.filter((option) => availableKeys.has(option.key));

    if (preserved.length > 0) {
      this.setValue(preserved, { noTouch: true });

      return;
    }

    this.reset();
  }

  toggle(key: string): void {
    if (key === ALL_NEWS_CATEGORY_KEY) {
      const isOnlyAllSelected = this.valueKeys.length === 1 && this.valueKeys[0] === ALL_NEWS_CATEGORY_KEY;

      this.setValue(isOnlyAllSelected ? [] : [this._allOption()]);

      return;
    }

    const option = this.optionsMap[key];

    if (!option) {
      return;
    }

    const withoutAll = this.value.filter((item) => item.key !== ALL_NEWS_CATEGORY_KEY);
    const isSelected = withoutAll.some((item) => item.key === key);

    if (isSelected) {
      this.setValue(withoutAll.filter((item) => item.key !== key));

      return;
    }

    this.setValue([...withoutAll, option]);
  }

  isSelected = (key: string): boolean => {
    return this.valueKeysSet.has(key);
  };

  reset(): void {
    const allOption = this._allOption();

    this.setValue([allOption], { noTouch: true });
    this.setDefaultValue([allOption]);
    this.unTouch();
  }

  private static async _getOptions(
    params: GetOptionsParams,
    organizationId: number,
  ): Promise<BaseResponse<{ items: NewsCategoryOption[]; total: number }>> {
    const response = await call<NewsCategoryOptionApi[]>({
      endpoint: ENDPOINTS.news.categories(String(organizationId)),
    });

    if (response.isError) {
      return {
        isError: true,
        error: response.error,
      };
    }

    const parsed = schemeParseMultiple(newsCategoryOptionSchema, response.data ?? []);
    const options = NewsCategoriesStore._withAllOption(parsed);

    if (params.keys.length > 0) {
      const keysSet = new Set(params.keys.map(String));
      const items = options.filter((option) => keysSet.has(option.key));

      return {
        isError: false,
        data: {
          items,
          total: items.length,
        },
      };
    }

    const filtered = NewsCategoriesStore._filterBySearch(options, params.search);
    const offset = Math.max(0, params.offset);
    const limit = params.limit ?? filtered.length;

    return {
      isError: false,
      data: {
        items: filtered.slice(offset, offset + limit),
        total: filtered.length,
      },
    };
  }

  private static _withAllOption(options: NewsCategoryOption[]): NewsCategoryOption[] {
    const withoutAll = options.filter((option) => option.key !== ALL_NEWS_CATEGORY_KEY);

    return [ALL_NEWS_CATEGORY_OPTION, ...withoutAll];
  }

  private static _filterBySearch(options: NewsCategoryOption[], search: string): NewsCategoryOption[] {
    const query = search?.trim().toLowerCase();

    if (!query) {
      return options;
    }

    return options.filter(
      (option) => option.key === ALL_NEWS_CATEGORY_KEY || option.value.toLowerCase().includes(query),
    );
  }

  private _allOption(): NewsCategoryOption {
    return this.optionsMap[ALL_NEWS_CATEGORY_KEY] ?? ALL_NEWS_CATEGORY_OPTION;
  }

  destroy(): void {
    super.destroy();
    this.meta.destroy();
  }
}
